import { computed } from 'vue'
import { useLocationStore } from '@/store/location'
import { useMainStore } from '@/store/main'
import { useSurveyStore } from '@/store/survey'

/**
 * Composable replacement for FunnelTypeMixin (global mixin).
 *
 * Provides reactive AREA flags, IS_MAIN_FUNNEL, IS_MALE_FUNNEL,
 * IS_FEMALE_FUNNEL, and IS_FEMALE_ACTIVITY_ONBOARDING_BUILDER_FUNNEL.
 *
 * Usage in setup():
 *   const { AREA, IS_MALE_FUNNEL } = useFunnelType()
 */
export function useFunnelType() {
	const locationStore = useLocationStore()
	const mainStore = useMainStore()
	const surveyStore = useSurveyStore()

	const appAreaConfig = computed(() => locationStore.getAppAreaConfig)
	const countryCode = computed(() => mainStore.getCountryCode)

	const AREA = computed(() => {
		const config = appAreaConfig.value
		if (!config) return {}
		const { appArea } = config

		const BAU_MAIN = ['main', 'main-al'].includes(appArea)
		const BAU_MAIN_AL = ['main-al'].includes(appArea)
		const BAU_FEMALE = [
			'female',
			'female-mediterranean-diet',
			'female-menopause-diet',
			'female-al',
			'female-water-balance',
			'carnivore-diet-female',
			'female-hashimoto-diet',
			'female-japanese-diet',
		].includes(appArea)
		const BAU_MALE = ['male', 'male-al', 'carnivore-diet-male', 'male-japanese-diet'].includes(appArea)
		const BAU_MALE_AL = ['male-al'].includes(appArea)
		const BAU_FEMALE_AL = ['female-al'].includes(appArea)
		const BAU = [BAU_MAIN, BAU_FEMALE, BAU_FEMALE_AL, BAU_MALE, BAU_MALE_AL, BAU_MAIN_AL].some(Boolean)

		const ACTIVITY_MAIN = ['main-activity'].includes(appArea)
		const ACTIVITY_FEMALE = ['activity-female-v2'].includes(appArea)
		const ACTIVITY_MALE = ['activity-male'].includes(appArea)
		const ACTIVITY = [ACTIVITY_MAIN, ACTIVITY_FEMALE, ACTIVITY_MALE].some(Boolean)

		const YOGA_MAIN = ['main-yoga'].includes(appArea)
		const YOGA_FEMALE = ['activity-female-yoga'].includes(appArea)
		const YOGA_FEMALE_AL = ['activity-female-yoga-al'].includes(appArea)
		const YOGA = [YOGA_MAIN, YOGA_FEMALE, YOGA_FEMALE_AL].some(Boolean)

		const WALKING_MAIN = ['main-walking'].includes(appArea)
		const WALKING_FEMALE = ['activity-female-walking'].includes(appArea)
		const WALKING_NORDIC_FEMALE_FWF = ['activity-female-nordic-walking-fwf'].includes(appArea)
		const WALKING_FEMALE_AL = ['activity-female-walking-al'].includes(appArea)
		const WALKING_MALE = ['activity-male-walking'].includes(appArea)
		const WALKING_JAPANESE_FEMALE = ['activity-female-walking-japanese'].includes(appArea)
		const WALKING_JAPANESE_FEMALE_V2 = ['activity-female-walking-japanese-v2'].includes(appArea)
		const WALKING_TAI_CHI_FEMALE = ['activity-female-tai-chi-walking'].includes(appArea)
		const WALKING_TAI_CHI_FEMALE_V2 = ['activity-female-tai-chi-walking-v2'].includes(appArea)
		const WALKING_TAI_CHI_FEMALE_AL = ['activity-female-tai-chi-walking-al'].includes(appArea)
		const WALKING_TAI_CHI_FEMALE_V2_AL = ['activity-female-tai-chi-walking-v2-al'].includes(appArea)
		const WALKING_TAI_CHI_FEMALE_PR = ['activity-female-tai-chi-walking-pr'].includes(appArea)
		const WALKING_TAI_CHI_MALE = ['activity-male-tai-chi-walking'].includes(appArea)
		const WALKING_TAI_CHI_MALE_AL = ['activity-male-tai-chi-walking-al'].includes(appArea)
		const PRINTABLE_WALKING_FEMALE = ['female-printable-walking'].includes(appArea)
		const WALKING = [
			WALKING_MAIN,
			WALKING_FEMALE,
			WALKING_NORDIC_FEMALE_FWF,
			WALKING_FEMALE_AL,
			WALKING_MALE,
			WALKING_JAPANESE_FEMALE,
			WALKING_JAPANESE_FEMALE_V2,
			WALKING_TAI_CHI_FEMALE,
			WALKING_TAI_CHI_FEMALE_V2,
			WALKING_TAI_CHI_FEMALE_AL,
			WALKING_TAI_CHI_MALE,
			WALKING_TAI_CHI_MALE_AL,
			PRINTABLE_WALKING_FEMALE,
			WALKING_TAI_CHI_FEMALE_V2_AL,
			WALKING_TAI_CHI_FEMALE_PR,
		].some(Boolean)

		const YOGA_TAI_CHI_MALE = ['activity-male-tai-chi-yoga'].includes(appArea)
		const YOGA_TAI_CHI_MALE_AL = ['activity-male-tai-chi-yoga-al'].includes(appArea)
		const YOGA_TAI_CHI_MALE_PR = ['activity-male-tai-chi-yoga-pr'].includes(appArea)
		const MAIN_TAI_CHI_WALKING = ['main-tai-chi-walking'].includes(appArea)
		const MAIN_TAI_CHI_WALKING_PR = ['main-tai-chi-walking-pr'].includes(appArea)
		const MAIN_TAI_CHI_WALKING_AL = ['main-tai-chi-walking-al'].includes(appArea)
		const TAI_CHI_FEMALE = ['activity-female-tai-chi'].includes(appArea)
		const MAIN_TAI_CHI = ['main-tai-chi'].includes(appArea)
		const MAIN_TAI_CHI_PR = ['main-tai-chi-pr'].includes(appArea)

		const PILATES_FEMALE = ['activity-female-pilates'].includes(appArea)
		const PILATES_FEMALE_AL = ['activity-female-pilates-al'].includes(appArea)
		const ASIAN_PILATES_FEMALE = ['activity-female-asian-pilates'].includes(appArea)
		const SENIOR_PILATES_FEMALE = ['activity-female-senior-pilates'].includes(appArea)
		const BOUDOIR_PILATES_FEMALE = ['activity-female-boudoir-pilates-fwf'].includes(appArea)

		const CHAIR_WORKOUTS_MALE = ['activity-male-chair-workouts'].includes(appArea)
		const CHAIR_WORKOUTS_MALE_AL = ['activity-male-chair-workouts-al'].includes(appArea)
		const CHAIR_TAI_CHI_FEMALE_FWF = ['activity-female-chair-tai-chi-fwf'].includes(appArea)
		const CHAIR_TAI_CHI_MALE = ['activity-male-chair-tai-chi'].includes(appArea)
		const TAI_CHI_GOLFERS_MALE_FWF = ['tai-chi-male-golfers-fwf'].includes(appArea)

		const CALISTHENICS_MAIN = ['main-calisthenics'].includes(appArea)
		const CALISTHENICS_MALE = ['activity-male-calisthenics'].includes(appArea)
		const CALISTHENICS_FEMALE = ['activity-female-calisthenics'].includes(appArea)

		const MILITARY_MALE = ['military-male'].includes(appArea)

		const CORTISOL_DIET_FEMALE = ['female-cortisol-diet'].includes(appArea)

		const MENOPAUSE_DIET_FEMALE = ['female-menopause-diet'].includes(appArea)

		const GLP_FEMALE = ['female-glp'].includes(appArea)
		const GLP_1 = ['glp-1'].includes(appArea)

		const WATER_BALANCE_FEMALE = ['female-water-balance'].includes(appArea)
		const WATER_FASTING_FEMALE = ['female-water-fasting'].includes(appArea)
		const WATER_FASTING_FEMALE_AL = ['female-water-fasting-al'].includes(appArea)
		const WATER_FASTING_MALE = ['male-water-fasting'].includes(appArea)

		const BAU_MAIN_PR = ['main-pr'].includes(appArea)
		const BAU_MALE_PR = ['male-pr'].includes(appArea)
		const BAU_FEMALE_PR = ['female-pr'].includes(appArea)

		const AYURVEDA_FEMALE = ['female-ayurveda-fwf'].includes(appArea)

		const MEDITERRANEAN_DIET_FEMALE = ['female-mediterranean-diet'].includes(appArea)

		const APPLOVIN = [
			BAU_FEMALE_AL,
			PILATES_FEMALE_AL,
			YOGA_FEMALE_AL,
			WALKING_FEMALE_AL,
			WALKING_TAI_CHI_FEMALE_AL,
			WALKING_TAI_CHI_MALE_AL,
			WALKING_TAI_CHI_FEMALE_V2_AL,
			MAIN_TAI_CHI_WALKING_AL,
			YOGA_TAI_CHI_MALE_AL,
			BAU_MALE_AL,
			BAU_MAIN_AL,
			WATER_FASTING_FEMALE_AL,
			CHAIR_WORKOUTS_MALE_AL,
		].some(Boolean)

		return {
			BAU,
			BAU_MAIN,
			BAU_FEMALE,
			BAU_FEMALE_AL,
			BAU_MALE,
			BAU_MALE_AL,
			BAU_MAIN_PR,

			ACTIVITY,
			ACTIVITY_MAIN,
			ACTIVITY_FEMALE,
			ACTIVITY_MALE,

			YOGA,
			YOGA_MAIN,
			YOGA_FEMALE,
			YOGA_FEMALE_AL,

			WALKING,
			WALKING_MAIN,
			WALKING_FEMALE,
			WALKING_NORDIC_FEMALE_FWF,
			WALKING_FEMALE_AL,
			WALKING_MALE,
			WALKING_JAPANESE_FEMALE,
			WALKING_JAPANESE_FEMALE_V2,
			WALKING_TAI_CHI_FEMALE,
			WALKING_TAI_CHI_MALE,
			WALKING_TAI_CHI_MALE_AL,
			WALKING_TAI_CHI_FEMALE_V2,
			WALKING_TAI_CHI_FEMALE_AL,
			WALKING_TAI_CHI_FEMALE_V2_AL,
			PRINTABLE_WALKING_FEMALE,
			WALKING_TAI_CHI_FEMALE_PR,

			MAIN_TAI_CHI_WALKING,
			MAIN_TAI_CHI_WALKING_PR,
			MAIN_TAI_CHI_WALKING_AL,
			MAIN_TAI_CHI,
			MAIN_TAI_CHI_PR,
			YOGA_TAI_CHI_MALE,
			YOGA_TAI_CHI_MALE_AL,
			YOGA_TAI_CHI_MALE_PR,
			TAI_CHI_FEMALE,

			PILATES_FEMALE,
			PILATES_FEMALE_AL,
			ASIAN_PILATES_FEMALE,
			SENIOR_PILATES_FEMALE,
			BOUDOIR_PILATES_FEMALE,

			CHAIR_WORKOUTS_MALE,
			CHAIR_WORKOUTS_MALE_AL,
			CHAIR_TAI_CHI_FEMALE_FWF,
			CHAIR_TAI_CHI_MALE,
			TAI_CHI_GOLFERS_MALE_FWF,

			CALISTHENICS_MAIN,
			CALISTHENICS_MALE,
			CALISTHENICS_FEMALE,

			MILITARY_MALE,

			CORTISOL_DIET_FEMALE,

			MENOPAUSE_DIET_FEMALE,

			MEDITERRANEAN_DIET_FEMALE,

			GLP_FEMALE,
			GLP_1,

			WATER_BALANCE_FEMALE,
			WATER_FASTING_FEMALE,
			WATER_FASTING_FEMALE_AL,
			WATER_FASTING_MALE,

			BAU_MALE_PR,

			AYURVEDA_FEMALE,

			BAU_FEMALE_PR,

			APPLOVIN,
		}
	})

	const IS_MAIN_FUNNEL = computed(() => {
		return AREA.value.BAU_MAIN
	})

	const IS_MALE_FUNNEL = computed(() => {
		return surveyStore.getPropertyValue('sex') === 'male'
	})

	const IS_FEMALE_FUNNEL = computed(() => {
		return surveyStore.getPropertyValue('sex') === 'female'
	})

	const IS_FEMALE_ACTIVITY_ONBOARDING_BUILDER_FUNNEL = computed(() => {
		const a = AREA.value
		return (
			a.ACTIVITY_FEMALE ||
			a.PILATES_FEMALE ||
			a.PILATES_FEMALE_AL ||
			a.BOUDOIR_PILATES_FEMALE ||
			a.YOGA_FEMALE ||
			a.YOGA_FEMALE_AL ||
			a.WALKING_FEMALE ||
			a.WALKING_NORDIC_FEMALE_FWF ||
			a.WALKING_FEMALE_AL ||
			a.WALKING_JAPANESE_FEMALE ||
			a.WALKING_JAPANESE_FEMALE_V2 ||
			a.ASIAN_PILATES_FEMALE ||
			a.SENIOR_PILATES_FEMALE ||
			a.WALKING_TAI_CHI_FEMALE ||
			a.WALKING_TAI_CHI_FEMALE_V2 ||
			a.PRINTABLE_WALKING_FEMALE ||
			a.WALKING_TAI_CHI_FEMALE_AL ||
			a.TAI_CHI_FEMALE ||
			a.CHAIR_TAI_CHI_FEMALE_FWF ||
			a.WALKING_TAI_CHI_FEMALE_V2_AL ||
			a.WALKING_TAI_CHI_FEMALE_PR
		)
	})

	return {
		appAreaConfig,
		countryCode,
		AREA,
		IS_MAIN_FUNNEL,
		IS_MALE_FUNNEL,
		IS_FEMALE_FUNNEL,
		IS_FEMALE_ACTIVITY_ONBOARDING_BUILDER_FUNNEL,
	}
}
